import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { BeneficiaryDedicatedTimeline } from "../components/BeneficiaryDedicatedTimeline";
import { BillsTab } from "../application/tabs/BillsTab";
import { MilestonesTab } from "../application/tabs/MilestonesTab";
import { dossierStatusChip } from "../application/statusUi";
import { PageHeader } from "../components/PageHeader";
import { useAppState } from "../context/AppStateContext";
import { ComingSoonTabPanel } from "../application/ComingSoonTabPanel";
import { areMilestonesAndBillsUnavailable } from "../application/milestoneBillsGate";

/** Beneficiary workspace from Projects → Beneficiaries → Open: timeline, bills, milestones. Full dossier via toolbar link. */
export function SubcontractorBeneficiaryDedicated() {
  const { id } = useParams<{ id: string }>();
  const { getById, getSubcontractorMaster, refreshApplicationsFromApi } = useAppState();
  const app = id ? getById(id) : undefined;
  const linkedSubMaster =
    app?.subcontractor.subcontractorMasterId != null
      ? getSubcontractorMaster(app.subcontractor.subcontractorMasterId)
      : undefined;
  const linkedSubcontractorName = linkedSubMaster?.firmName?.trim() ?? "";

  useEffect(() => {
    void refreshApplicationsFromApi();
  }, [id, refreshApplicationsFromApi]);

  if (!id || !app) {
    return (
      <>
        <PageHeader
          breadcrumb={[
            { label: "Home", to: "/" },
            { label: "Beneficiaries", to: "/subcontractor-to-beneficiary" },
            { label: "Not found" },
          ]}
          title="Application not found"
          subtitle="Return to the list and choose another beneficiary."
        />
        <Link to="/subcontractor-to-beneficiary" className="btn btn--primary btn--tg">
          Back to beneficiaries
        </Link>
      </>
    );
  }

  const hideBillsMilestonesHub = areMilestonesAndBillsUnavailable(app);

  return (
    <div className="dedicated-subcontractor-link beneficiary-dedicated-hub">
      <PageHeader
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Beneficiaries", to: "/subcontractor-to-beneficiary" },
          { label: app.mobile.beneficiaryName },
        ]}
        title="Beneficiary detail"
        subtitle={
          <>
            <div className="dedicated-subcontractor-link__meta">
              <span className="mono">{app.mobile.applicationId}</span>
              <span aria-hidden="true">·</span>
              <span>
                Sanction <span className="mono">{app.mobile.houseSanctionNumber}</span>
              </span>
            </div>
            <div className="dedicated-subcontractor-link__title-row">
              <span className="dedicated-subcontractor-link__beneficiary-name">{app.mobile.beneficiaryName}</span>
              {app.subcontractor.subcontractorMasterId ? (
                <>
                  <span className="dedicated-subcontractor-link__title-sep" aria-hidden="true">
                    ·
                  </span>
                  <div className="dedicated-subcontractor-link__subcontractor">
                    <span className="dedicated-subcontractor-link__subcontractor-label">Subcontractor</span>
                    <span className="dedicated-subcontractor-link__subcontractor-name">
                      {linkedSubcontractorName || "Onboarded firm record not found"}
                    </span>
                  </div>
                </>
              ) : null}
            </div>
          </>
        }
        badge={dossierStatusChip(app)}
      />

      <div className="toolbar-row dedicated-subcontractor-link__toolbar">
        <Link to="/subcontractor-to-beneficiary" className="btn btn--tg-secondary btn--sm">
          ← Back to list
        </Link>
        <Link to={`/applications/${app.id}/overview`} className="btn btn--link btn--sm">
          Open full dossier (all sections)
        </Link>
      </div>

      <section className="beneficiary-dedicated-hub__section" aria-labelledby="beneficiary-hub-timeline">
        <h2 id="beneficiary-hub-timeline" className="beneficiary-dedicated-hub__h">
          Timeline tracking
        </h2>
        <div className="card card--elevated beneficiary-dedicated-hub__panel beneficiary-dedicated-hub__panel--gantt">
          <BeneficiaryDedicatedTimeline app={app} />
        </div>
      </section>

      {hideBillsMilestonesHub ? (
        <section className="beneficiary-dedicated-hub__section" aria-labelledby="beneficiary-hub-deferred">
          <h2 id="beneficiary-hub-deferred" className="beneficiary-dedicated-hub__h">
            Bills &amp; milestones
          </h2>
          <ComingSoonTabPanel
            title="Bills & milestones — coming soon"
            appId={app.id}
            body="Bills and milestone tracking are not available in this console yet — before or after subcontractor linkage. Open the full dossier from the link above when your office enables these sections."
          />
        </section>
      ) : (
        <>
          <section className="beneficiary-dedicated-hub__section" aria-labelledby="beneficiary-hub-bills">
            <h2 id="beneficiary-hub-bills" className="beneficiary-dedicated-hub__h">
              Bills
            </h2>
            <p className="beneficiary-dedicated-hub__lead">
              Work-done bill references and site officer seeding confirmation.
            </p>
            <BillsTab />
          </section>

          <section className="beneficiary-dedicated-hub__section" aria-labelledby="beneficiary-hub-milestones">
            <h2 id="beneficiary-hub-milestones" className="beneficiary-dedicated-hub__h">
              Work progress
            </h2>
            <p className="beneficiary-dedicated-hub__lead">
              Milestone phases from master data — mark items done and link bills per phase.
            </p>
            <MilestonesTab />
          </section>
        </>
      )}
    </div>
  );
}
